/**
 * F4 — Cost attribution dashboard.
 *
 * Rolls request_log.estimated_cost_usd up by provider / real_key / project /
 * proxy_key. Operator picks the dimension + window and the table re-ranks.
 *
 * Per-model attribution isn't here yet because request_log doesn't capture
 * the model name — adding it requires parsing the request body in the
 * proxy hot path.
 */
import { useEffect, useState } from "react";
import { api } from "@/api/client";

type GroupBy = "provider" | "real_key" | "project" | "proxy_key";

type CostRow = {
  group_id: string | null;
  label: string;
  requests: number;
  errors: number;
  error_rate_pct: number;
  total_cost_usd: number;
  pct_of_total: number;
};

type CostResp = {
  window_days: number;
  since: string;
  group_by: GroupBy;
  items: CostRow[];
  total_cost_usd: number;
  total_requests: number;
};

type TsPoint = { day: string; requests: number; cost: number };
type TsResp = { window_days: number; series: TsPoint[] };

const GROUP_OPTIONS: { v: GroupBy; label: string }[] = [
  { v: "provider", label: "Provider" },
  { v: "real_key", label: "Real key" },
  { v: "project", label: "Project" },
  { v: "proxy_key", label: "Proxy key" },
];

const DAYS_OPTIONS = [7, 30, 90];

function fmtUsd(n: number): string {
  if (n === 0) return "$0";
  if (n < 0.01) return "<$0.01";
  if (n < 1) return `$${n.toFixed(3)}`;
  return `$${n.toFixed(2)}`;
}

export function Costs() {
  const [days, setDays] = useState(30);
  const [groupBy, setGroupBy] = useState<GroupBy>("provider");
  const [data, setData] = useState<CostResp | null>(null);
  const [ts, setTs] = useState<TsResp | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api<CostResp>(`/api/admin/costs?days=${days}&group_by=${groupBy}`),
      api<TsResp>(`/api/admin/costs/timeseries?days=${Math.min(days, 90)}`),
    ])
      .then(([d, t]) => {
        if (cancelled) return;
        setData(d);
        setTs(t);
        setErr(null);
      })
      .catch((e: any) => !cancelled && setErr(e?.message ?? "load failed"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [days, groupBy]);

  const max = Math.max(...(ts?.series.map((p) => p.cost) ?? [0]), 0.0001);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-4xl display tracking-tight">Cost attribution</h1>
        <p className="text-sm text-slate-400 mt-1">
          Estimated upstream spend, rolled up by the dimension you choose. Cost
          is computed from <code>pricing.estimate_request_cost_usd</code> at log
          time using bytes-up / bytes-down approximated to tokens. Only 2xx
          responses contribute.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">Window</span>
          {DAYS_OPTIONS.map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={
                "text-xs px-3 py-1.5 rounded border " +
                (days === d
                  ? "bg-amber-500/15 border-amber-500/40 text-amber-200"
                  : "border-white/10 hover:bg-white/5")
              }
            >
              {d}d
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">Group by</span>
          {GROUP_OPTIONS.map((o) => (
            <button
              key={o.v}
              onClick={() => setGroupBy(o.v)}
              className={
                "text-xs px-3 py-1.5 rounded border " +
                (groupBy === o.v
                  ? "bg-amber-500/15 border-amber-500/40 text-amber-200"
                  : "border-white/10 hover:bg-white/5")
              }
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {err && <div className="text-rose-300 text-sm">{err}</div>}

      {data && (
        <div className="grid grid-cols-3 gap-4">
          <div className="card">
            <div className="text-xs text-slate-400">Total spend</div>
            <div className="text-3xl mono mt-1">{fmtUsd(data.total_cost_usd)}</div>
            <div className="text-xs text-slate-500 mt-1">{data.window_days}d window</div>
          </div>
          <div className="card">
            <div className="text-xs text-slate-400">Total requests</div>
            <div className="text-3xl mono mt-1">{data.total_requests.toLocaleString()}</div>
          </div>
          <div className="card">
            <div className="text-xs text-slate-400">Avg per request</div>
            <div className="text-3xl mono mt-1">
              {fmtUsd(data.total_requests ? data.total_cost_usd / data.total_requests : 0)}
            </div>
          </div>
        </div>
      )}

      {ts && (
        <div className="card">
          <div className="text-xs text-slate-400 mb-2">
            Daily spend (last {ts.window_days}d, all keys)
          </div>
          <div className="flex items-end gap-1 h-32">
            {ts.series.map((p) => (
              <div
                key={p.day}
                className="flex-1 bg-amber-500/40 hover:bg-amber-500/70 rounded-t"
                style={{ height: `${(p.cost / max) * 100}%`, minHeight: "2px" }}
                title={`${p.day}: ${fmtUsd(p.cost)} · ${p.requests} req`}
              />
            ))}
          </div>
        </div>
      )}

      {data && (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-400 uppercase">
              <tr className="border-b border-white/10">
                <th className="text-left py-2 px-3">{groupBy.replace("_", " ")}</th>
                <th className="text-right py-2 px-3">Cost (USD)</th>
                <th className="text-right py-2 px-3">% of total</th>
                <th className="text-right py-2 px-3">Requests</th>
                <th className="text-right py-2 px-3">Error rate</th>
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-slate-500">
                    No spend recorded in this window.
                  </td>
                </tr>
              )}
              {data.items.map((row, i) => (
                <tr key={`${row.group_id}-${i}`} className="border-b border-white/5 hover:bg-white/5">
                  <td className="py-2 px-3 font-medium">{row.label}</td>
                  <td className="py-2 px-3 text-right mono">{fmtUsd(row.total_cost_usd)}</td>
                  <td className="py-2 px-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-16 h-1.5 bg-white/5 rounded overflow-hidden">
                        <div
                          className="h-full bg-amber-500/60"
                          style={{ width: `${row.pct_of_total}%` }}
                        />
                      </div>
                      <span className="text-xs text-slate-400 w-12 text-right">
                        {row.pct_of_total.toFixed(1)}%
                      </span>
                    </div>
                  </td>
                  <td className="py-2 px-3 text-right mono text-slate-300">
                    {row.requests.toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <span
                      className={
                        row.error_rate_pct > 5
                          ? "text-rose-300"
                          : row.error_rate_pct > 1
                          ? "text-amber-300"
                          : "text-slate-400"
                      }
                    >
                      {row.error_rate_pct.toFixed(2)}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {loading && <div className="text-xs text-slate-500">Loading…</div>}
    </div>
  );
}
